import { refreshUserCredentials } from '../actions/application';
import { getStore } from '../store';
import { URLS } from './url';

export const HttpHeaders = {
  ISOTOPE_CREDENTIALS: 'Isotope-Credentials',
  ISOTOPE_SALT: 'Isotope-Salt',
  CONTENT_TYPE: 'Content-Type'
};

export const HttpStatusFamilies = {
  INFORMATIONAL: 1,
  SUCCESSFUL: 2,
  REDIRECTION: 3,
  CLIENT_ERROR: 4,
  SERVER_ERROR: 5
};

export class AuthenticationException extends Error {
  constructor(message, cause) {
    super(message);
    this.cause = cause;
    this.name = 'AuthenticationException';
  }
}

/**
 * Object to store the different AbortController(s) that will be used in the service methods to fetch from the API backend.
 *
 * @type {Object}
 * @private
 */
export const abortControllerWrappers = {};

/**
 * Converts response Promise to json Promise or throws an error.
 *
 * @param {Response}response
 * @returns {PromiseLike<object>}
 */
export function toJson(response) {
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new AuthenticationException(response.statusText);
    } else {
      throw Error(`${response.status}`);
    }
  }
  return response.json();
}

/**
 * Converts response Promise to text Promise or throws an error.
 *
 * @param {Response}response
 * @returns {PromiseLike<object>}
 */
export function toText(response) {
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new AuthenticationException(response.statusText);
    } else {
      throw Error(`${response.status}`);
    }
  }
  return response.text();
}

/**
 * Dispatches the refreshUserCredentials action with the values obtained from the Http Response headers.
 *
 * @param dispatch
 * @param response
 * @returns {*}
 */
export function refreshCredentials(dispatch, response) {
  const credentials = response.headers.get(HttpHeaders.ISOTOPE_CREDENTIALS);
  const salt = response.headers.get(HttpHeaders.ISOTOPE_SALT);

  if (credentials && salt) {
    dispatch(refreshUserCredentials(credentials, salt));
  }

  return Promise.resolve(response);
}

/**
 * Triggers the abort method of a the specified AbortController
 *
 * @param abortController {AbortController}
 */
export function abortFetch(abortController) {
  if (abortController && abortController.abort) {
    // Defer abort to next tick to allow pending promise chains to attach catch handlers
    setTimeout(() => {
      try {
        abortController.abort();
      } catch (e) {
        console.error('Error while aborting fetch:', e);
      }
    }, 0);
  }
}

/**
 * Creates a new headers object including Isotope specific credentials headers for server authentication.
 *
 * @param {{}} credentials
 * @param {{}} [originalHeaders={}] originalHeaders
 * @returns {{}}
 */
export function credentialsHeaders(credentials, originalHeaders) {
  const newHeaders = Object.assign({}, originalHeaders ? originalHeaders : {});
  newHeaders[HttpHeaders.ISOTOPE_CREDENTIALS] = credentials.encrypted;
  newHeaders[HttpHeaders.ISOTOPE_SALT] = credentials.salt;
  return newHeaders;
}

/**
 * Returns true if the response the Successful HTTP status code range (2xx)
 *
 * @param HTTP status code of the response
 * @returns {boolean} true if status code belongs to HttpStatusFamilies.SUCCESSFUL family
 */
export function isSuccessful(status) {
  return Math.floor(status / 100) === HttpStatusFamilies.SUCCESSFUL;
}

// ---------------------------------------------------------------------------
// PEC-8747 — sesión con imapserver: cabeceras Isotope desde el store, refresco del token en toda
// respuesta y renovación silenciosa ante 401 (una llamada a /login + un reintento de la petición).
// Nunca borra credenciales, nunca navega, nunca recarga: el editor y el borrador siguen montados.
// ---------------------------------------------------------------------------

/** Parámetros de la renovación (mutables para tests). */
export const SESSION_RENEWAL = {
  retryDelayMs: 1000,
  retryJitterMs: 1000,
  cooldownMs: 60000
};

const sessionState = {
  renewing: null, // Promise<boolean> en vuelo (single-flight)
  cooldownUntil: 0, // epoch ms hasta el que no se vuelve a llamar a /login
  cooldownToken: null // token con el que falló la renovación; si cambia, el cooldown se levanta
};

/** Reinicia el estado interno de la renovación (solo para tests). */
export function resetSessionStateForTests() {
  sessionState.renewing = null;
  sessionState.cooldownUntil = 0;
  sessionState.cooldownToken = null;
}

function safeStore() {
  try {
    return getStore();
  } catch {
    return null;
  }
}

function storeDispatch(action) {
  const store = safeStore();
  if (store) {
    store.dispatch(action);
  }
}

/**
 * Credenciales Isotope vigentes en el store, o null si no hay sesión.
 *
 * @returns {{encrypted: string, salt: string}|null}
 */
export function currentCredentials() {
  const store = safeStore();
  const state = store && store.getState();
  const credentials = state && state.application && state.application.user && state.application.user.credentials;
  return credentials && credentials.encrypted && credentials.salt ? credentials : null;
}

/**
 * Aplica al store el token renovado que imapserver escribe en las cabeceras de toda respuesta autenticada
 * (CredentialsRefreshFilter). Acepta un Headers de fetch, el objeto `headers` del evento `open` del
 * EventSourcePolyfill o un XMLHttpRequest.
 *
 * @returns {boolean} true si había token nuevo
 */
export function refreshFromHeaders(headers) {
  if (!headers) {
    return false;
  }
  const read = name => {
    if (typeof headers.get === 'function') {
      return headers.get(name);
    }
    if (typeof headers.getResponseHeader === 'function') {
      return headers.getResponseHeader(name);
    }
    return null;
  };
  const encrypted = read(HttpHeaders.ISOTOPE_CREDENTIALS);
  const salt = read(HttpHeaders.ISOTOPE_SALT);
  if (encrypted && salt) {
    storeDispatch(refreshUserCredentials(encrypted, salt));
    return true;
  }
  return false;
}

/**
 * Las URLs de imapserver (relativas o HATEOAS absolutas) se piden siempre relativas al origen:
 * en K8s Traefik enruta /api al imapserver y en desarrollo lo hace el proxy de Vite.
 */
export function toImapServerUrl(url) {
  const value = url instanceof URL ? url.href : String(url);
  const idx = value.indexOf('/api/');
  return idx !== -1 ? value.substring(idx) : value;
}

function withSessionHeaders(init) {
  const headers = new Headers(init && init.headers ? init.headers : {});
  const credentials = currentCredentials();
  if (credentials) {
    headers.set(HttpHeaders.ISOTOPE_CREDENTIALS, credentials.encrypted);
    headers.set(HttpHeaders.ISOTOPE_SALT, credentials.salt);
  }
  return { ...init, headers };
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

function clearCooldown() {
  sessionState.cooldownUntil = 0;
  sessionState.cooldownToken = null;
}

async function requestNewCredentials() {
  const store = safeStore();
  const state = store && store.getState();
  const formValues = state && state.login && state.login.formValues;
  if (!formValues || !formValues.user) {
    return false;
  }
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) {
      await sleep(SESSION_RENEWAL.retryDelayMs + Math.random() * SESSION_RENEWAL.retryJitterMs);
    }
    try {
      const response = await fetch(URLS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      });
      if (response.ok) {
        const credentials = await response.json();
        if (credentials && credentials.encrypted && credentials.salt) {
          storeDispatch(refreshUserCredentials(credentials.encrypted, credentials.salt));
          return true;
        }
        return false;
      }
      // 401 (hoy también por fallos transitorios del proveedor) o 5xx: un reintento más
    } catch {
      // error de red: un reintento más
    }
  }
  return false;
}

/**
 * Renueva la sesión Isotope contra /login con las credenciales del formulario guardadas en el store.
 * Single-flight: las llamadas concurrentes comparten la misma promesa. Tras un fallo definitivo entra en
 * cooldown y devuelve false sin llamar a /login hasta que pase el tiempo, cambie el token o una respuesta
 * autenticada sea 2xx.
 *
 * @param {string} [staleToken] token que recibió el 401; si el store ya tiene otro no hace falta renovar
 * @returns {Promise<boolean>} true si el store tiene un token válido nuevo
 */
export function renewSession(staleToken) {
  const current = currentCredentials();
  if (staleToken && current && current.encrypted !== staleToken) {
    return Promise.resolve(true);
  }
  if (sessionState.renewing) {
    return sessionState.renewing;
  }
  if (Date.now() < sessionState.cooldownUntil && (!current || current.encrypted === sessionState.cooldownToken)) {
    return Promise.resolve(false);
  }
  sessionState.renewing = requestNewCredentials()
    .then(renewed => {
      if (renewed) {
        clearCooldown();
      } else {
        sessionState.cooldownUntil = Date.now() + SESSION_RENEWAL.cooldownMs;
        sessionState.cooldownToken = current ? current.encrypted : null;
      }
      return renewed;
    })
    .finally(() => {
      sessionState.renewing = null;
    });
  return sessionState.renewing;
}

/**
 * fetch hacia imapserver con gestión de sesión. Devuelve siempre la Response, también la 401 cuando la
 * renovación falla: el contrato de los llamadores (toJson/toText/response.ok) no cambia.
 *
 * @param {string|URL} url
 * @param {RequestInit} [init]
 * @param {{retry?: boolean}} [options] retry=false desactiva la renovación (p. ej. en el propio /login)
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, init = {}, { retry = true } = {}) {
  const requestUrl = toImapServerUrl(url);
  const firstInit = withSessionHeaders(init);
  const usedToken = firstInit.headers.get(HttpHeaders.ISOTOPE_CREDENTIALS);
  const response = await fetch(requestUrl, firstInit);
  refreshFromHeaders(response.headers);
  if (response.ok) {
    clearCooldown();
  }
  const aborted = () => Boolean(init.signal && init.signal.aborted);
  if (response.status !== 401 || !retry || aborted()) {
    return response;
  }
  const renewed = await renewSession(usedToken);
  if (!renewed || aborted()) {
    return response;
  }
  const retryResponse = await fetch(requestUrl, withSessionHeaders(init));
  refreshFromHeaders(retryResponse.headers);
  if (retryResponse.ok) {
    clearCooldown();
  }
  return retryResponse;
}

/**
 * POST JSON por XMLHttpRequest (necesario para el progreso de subida) con la misma gestión de sesión que
 * apiFetch: un 401 renueva y repite la petición una sola vez. Seguro porque el filtro de autenticación
 * responde antes de que el controlador procese nada.
 *
 * @param {string} url
 * @param {string} payload JSON ya serializado
 * @param {{credentials?: object, onProgress?: function, onLoad: function, onError: function}} handlers
 */
export function xhrPostWithSession(url, payload, { credentials, onProgress, onLoad, onError }) {
  const attempt = retry => {
    const current = currentCredentials() || credentials;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', toImapServerUrl(url));
    if (current) {
      xhr.setRequestHeader(HttpHeaders.ISOTOPE_CREDENTIALS, current.encrypted);
      xhr.setRequestHeader(HttpHeaders.ISOTOPE_SALT, current.salt);
    }
    xhr.setRequestHeader(HttpHeaders.CONTENT_TYPE, `application/json; charset=${document.characterSet}`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = onProgress;
    }
    xhr.onload = event => {
      refreshFromHeaders(xhr);
      if (xhr.status === 401 && retry) {
        renewSession(current ? current.encrypted : null).then(renewed => {
          if (renewed) {
            attempt(false);
          } else {
            onLoad(event);
          }
        });
        return;
      }
      if (isSuccessful(xhr.status)) {
        clearCooldown();
      }
      onLoad(event);
    };
    xhr.onerror = onError;
    xhr.send(payload);
  };
  attempt(true);
}
